import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';

const GoalDistribution = ({ teams, matches }) => {
  const [timeDistribution, setTimeDistribution] = useState([]);
  const [teamDistribution, setTeamDistribution] = useState([]);
  
  useEffect(() => {
    // Calculate goal distribution by time
    const calculateTimeDistribution = () => {
      // Time buckets (in minutes): 0-10, 11-20, 21-30, etc.
      const buckets = [
        { label: '0-5', count: 0 },
        { label: '6-10', count: 0 },
        { label: '11-15', count: 0 },
        { label: 'Last Min', count: 0 } // For goals in the final minute
      ];
      
      matches.forEach(match => {
        match.events
          .filter(event => event.type === 'goal')
          .forEach(event => {
            // In a real app, we'd have the actual match time for each goal
            // Here we'll use a simplified approach using the event timestamp
            
            // Calculate minutes since match start (for demo purposes)
            const matchStartTime = new Date(match.created).getTime();
            const goalTime = new Date(event.time).getTime();
            const minutesSinceStart = Math.floor((goalTime - matchStartTime) / (1000 * 60));
            
            // Determine which bucket this goal belongs to
            if (minutesSinceStart <= 5) {
              buckets[0].count += 1;
            } else if (minutesSinceStart <= 10) {
              buckets[1].count += 1;
            } else if (minutesSinceStart <= 15) {
              buckets[2].count += 1;
            } else {
              buckets[3].count += 1; // Last minute bucket
            }
          });
      });
      
      setTimeDistribution(buckets);
    };
    
    // Calculate goal distribution by team
    const calculateTeamDistribution = () => {
      const distribution = teams.map(team => {
        let homeGoals = 0;
        let awayGoals = 0;
        
        matches.forEach(match => {
          if (match.homeTeam.id === team.id) {
            homeGoals += match.homeScore;
          }
          if (match.awayTeam.id === team.id) {
            awayGoals += match.awayScore;
          }
        });
        
        return {
          id: team.id,
          name: team.name,
          color: team.color,
          homeGoals,
          awayGoals,
          totalGoals: homeGoals + awayGoals
        };
      });
      
      setTeamDistribution(distribution.sort((a, b) => b.totalGoals - a.totalGoals));
    };
    
    calculateTimeDistribution();
    calculateTeamDistribution();
  }, [teams, matches]);
  
  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-lg font-semibold mb-3">Goals by Team</h2>
        
        <div className="space-y-3 mb-2">
          {teamDistribution.map(team => {
            const maxGoals = Math.max(...teamDistribution.map(t => t.totalGoals));
            const percentage = team.totalGoals > 0 ? (team.totalGoals / maxGoals) * 100 : 0;
            
            return (
              <div key={team.id} className="space-y-1">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: team.color }}
                    />
                    <span className="font-medium text-sm">{team.name}</span>
                  </div>
                  <span className="text-sm font-medium">{team.totalGoals} goals</span>
                </div>
                
                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full flex"
                    style={{ width: `${percentage}%` }}
                  >
                    <div 
                      className="h-full" 
                      style={{ 
                        backgroundColor: team.color, 
                        width: `${team.homeGoals / team.totalGoals * 100}%` 
                      }}
                    />
                    <div 
                      className="h-full" 
                      style={{ 
                        backgroundColor: `${team.color}80`, // Add transparency
                        width: `${team.awayGoals / team.totalGoals * 100}%` 
                      }}
                    />
                  </div>
                </div>
                
                <div className="flex text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: team.color }} />
                    <span>Home: {team.homeGoals}</span>
                  </div>
                  <div className="ml-3 flex items-center">
                    <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: `${team.color}80` }} />
                    <span>Away: {team.awayGoals}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      
      <Card>
        <h2 className="text-lg font-semibold mb-3">Goals by Time</h2>
        
        <div className="space-y-3">
          {timeDistribution.map((bucket, index) => {
            const maxCount = Math.max(...timeDistribution.map(b => b.count));
            const percentage = bucket.count > 0 ? (bucket.count / maxCount) * 100 : 0;
            
            // Use different colors for different time periods
            const colors = ['#4CAF50', '#8BC34A', '#FFC107', '#FF5722'];
            
            return (
              <div key={index} className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm">{bucket.label} min</span>
                  <span className="text-sm font-medium">{bucket.count} goals</span>
                </div>
                
                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: colors[index]
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Note: In a full app, precise time tracking would show exact goal minutes.
        </div>
      </Card>
    </div>
  );
};

export default GoalDistribution;
